import { useQuery } from '@tanstack/react-query'
import to from 'await-to-js'
import { AxiosError } from 'axios'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import TableButtonProduct from 'components/Buttons/TableButtons/TableButtonProduct'
import { DataTable, useDataTableContext } from 'components/DataTable'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import DistributorAssignModal from 'components/Modals/AssignmentModal/DistributorAssignModal'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import {
  CreateContactRequestJobModal,
  MAX_OPPORTUNITIES_REQUESTED,
} from 'components/Modals/CreateContactRequestJobModal'
import ChainExportModal from 'components/Modals/ExportModal/ChainExportModal'
import MenuModal from 'components/Modals/MenuModal/MenuModal'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'
import { ChainsFilterset } from 'components/Tables/ChainsTable/ChainsFilterset'
import { CHAIN_COLUMNS } from 'components/DataExpansionComponents/Chains/chainColumnsInfo'
import { CHAIN_SUMMARY_KEY } from 'constants/tableQueryKeys'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import {
  PiExport,
  PiForkKnife,
  PiGlobe,
  PiHandshake,
  PiPhone,
  PiPushPin,
  PiRocketLaunch,
  PiStorefront,
  PiTag,
  PiTruck,
  PiXCircle,
} from 'react-icons/pi'
import useApiService from 'services/api'
import { notifyError } from 'services/toast'
import { useSelectedProductIds } from 'stores/SelectedProductsStore/SelectedProductsStore'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { paramsContainAccountFilters } from 'utils/filterUtils'
import { AssignToCampaignModal } from '../../../features/campaigns/AssignToCampaignModal'
import { useCampaignUnassigner } from '../../../features/campaigns/useCampaignUnassigner'
import { FilterIdentifier } from '../../../models/saved_view'
import { ChainsSummary } from '../../../models/summaries'
import { useOpportunitiesContext } from '../../../pages/app/opportunities/opportunities-context'
import { queryClient } from '../../../services/queryClient'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import { CreateDealModalForTable } from '../../Deals/CreateDealModal/CreateDealModal'

import { TableSearchWithElastic } from '../../Filters/TableSearchWithElastic'
import { IDropdownItem } from '../../UI/Dropdown/Dropdown'
import { ChangeProductViewModal } from '../../change-product-view-modal'
import * as S from '../CommonTable.styles'

import { usePreferences } from 'context/preferences/PreferencesContext'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../DataTable/UI'
import { cn } from '../../UI/cn'
import { ManageTagsDistributorsParams } from 'services/common_table_actions'
import { SiSalesforce } from 'react-icons/si'
import { toast } from 'react-toastify'
import GPOAssignModal from 'components/Modals/AssignmentModal/GPOAssignModal'
import { PiCalendarStar, PiShippingContainer } from 'react-icons/pi'
import { IChainTable } from './ChainsTable'
import {
  sortableFieldOptions,
  sortableFields,
  useChainsTableColumnsV2,
} from './ChainsTableColumnsV2'
import ReminderModal from 'components/Reminders/ReminderModal'
import TableButtonSort from 'components/Buttons/TableButtons/TableButtonSort'
import { useImperativePin } from 'components/QuickAccess/usePinTarget'

const PAGE_SIZE = 100

export function ChainsTableComponentV2(props: IChainTable) {
  const api = useApiService()
  const featureFlag = useFeatureFlagService()
  const pin = useImperativePin()
  const hasCampaign = !!props.baseFilters?.main_campaign
  const linkVariant = useTableLinkVariant()
  const IDENTIFIER = 'CHAINS_TABLE' + (props.filterIdentifierModifier ?? '')
  const chainsFilterStore = filterStoreRepo.getStore(
    IDENTIFIER as FilterIdentifier
  )

  const { page, setPage, search } = chainsFilterStore()

  const { sorting, setSorting } = dataTableSortingStoreRepo.getStore(
    props.tableKey
  )()

  // Set default sort to chain count descending if no sort is set
  useEffect(() => {
    if (!sorting.length) {
      setSorting([{ id: 'count', desc: true }])
    }
    // Only run on mount
  }, [])

  const {
    state: { rowSelection, totalSelectedRows, isAllRowsSelected },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()

  const { selectedTag } = useOpportunitiesContext()
  const { preferences } = usePreferences()

  const [showProductSelection, setShowProductSelection] = useState(false)
  const [showAssignTags, setShowAssignTags] = useState(false)
  const [showAssignDistributors, setShowAssignDistributors] = useState(false)
  const [showAssignGPOs, setShowAssignGPOs] = useState(false)
  const [showContactRequest, setShowContactRequest] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)

  const [menuModal, setMenuModal] = useState<
    | {
        chainId: number
        chainName?: string
        itemsIds?: number[]
        menuLength?: number
      }
    | undefined
  >(undefined)

  const selectedProductsIds = useSelectedProductIds()

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0].id : sorting[0].id
    }

    return params
  }, [sorting])

  // Check if any elastic search is active (name, menu, or domain)
  const hasActiveSearch = useMemo(() => {
    return Object.keys(search).some((key) => search[key]?.value)
  }, [search])

  // Reset relevancy sort to count when search is cleared
  useEffect(() => {
    if (!hasActiveSearch && sorting.length && sorting[0].id === 'relevancy') {
      setSorting([{ id: 'count', desc: true }])
    }
  }, [hasActiveSearch, sorting, setSorting])

  // Filter sort options - only show relevancy when a search is active
  const filteredSortOptions = useMemo(() => {
    if (hasActiveSearch) {
      return sortableFieldOptions
    }
    return sortableFieldOptions.filter((opt) => opt.value !== 'relevancy')
  }, [hasActiveSearch])

  const filtersAsParams = {
    ...useFilterParams(chainsFilterStore),
    ...props.baseFilters,
  }

  const filterAndSortParams = useMemo(() => {
    return {
      ...filtersAsParams,
      ...sortParams,
    }
  }, [filtersAsParams, sorting, props.baseFilters])

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    products: selectedProductsIds,
    page: page,
  })

  // DATA FETCHING
  const { isFetching, data } = useQuery({
    staleTime: Infinity,
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      clearSelectedRows()

      const [err, res] = await to(
        api.getChainsWithCRMReports(
          {
            ...filterAndSortParams,
            page: page,
            product_id: selectedProductsIds,
          },
          signal
        )
      )

      if ((err as AxiosError)?.code === 'ERR_NETWORK') {
        notifyError('Sorry! Unable to complete this search, please try again.')
      }
      if (err) throw err

      return res
    },
  })
  // END DATA FETCHING

  const unassignFromCampaign = useCampaignUnassigner('chains-reports-table', {
    data: data?.results ?? [],
    accessorKey: 'contact_company_id',
    tableQueryKey: TABLE_QUERY_KEY,
    requestParams: filterAndSortParams,
    campaignId: props?.baseFilters?.main_campaign,
    setPage: setPage,
    totalCount: data?.count ?? 0,
    pageSize: PAGE_SIZE,
  })

  useEffect(() => {
    clearSelectedRows()
  }, [data])

  const [openDealModal, setOpenDealModal] = React.useState(false)

  const summaryAbortControllerV2 = useMemo(() => new AbortController(), [])

  const totalRowsSelected = useMemo(() => {
    return isAllRowsSelected
      ? totalSelectedRows
      : Object.keys(rowSelection).length
  }, [isAllRowsSelected, rowSelection])

  const { data: chainsSummary, isLoading: chainsSummaryLoading } =
    api.useGetChainReportsSummary(
      {
        ...filtersAsParams,
        product_id: selectedProductsIds.filter((p) => p !== -1),
      },
      summaryAbortControllerV2.signal
    )

  // Track previous summary to preserve values during loading
  const previousSummaryRef = useRef<ChainsSummary>({
    total_ltv: 0,
    places_count: 0,
    count: 0,
    avg_pounds_per_year: 0,
    avg_total_ltv: 0,
    isLoading: true,
  })

  useEffect(() => {
    if (chainsSummaryLoading) {
      // Show loading state but preserve previous values to avoid flickering
      props.setChainsSummary?.({
        ...previousSummaryRef.current,
        isLoading: true,
      })
      return
    }

    // Update with new data when ready
    const newSummary = {
      total_ltv: chainsSummary?.total_ltv ?? 0,
      places_count: chainsSummary?.places_count ?? 0,
      count: chainsSummary?.count ?? 0,
      avg_pounds_per_year: chainsSummary?.avg_pounds_per_year ?? 0,
      avg_total_ltv: chainsSummary?.avg_total_ltv ?? 0,
      isLoading: false,
    }
    previousSummaryRef.current = newSummary
    props.setChainsSummary?.(newSummary)
    setTotalRowsInBackend(chainsSummary?.count ?? 0)
  }, [
    chainsSummaryLoading,
    chainsSummary,
    props.setChainsSummary,
    setTotalRowsInBackend,
  ])

  const handleCreateDeals = () => {
    if (!data) return
    setOpenDealModal(true)
  }

  const [showExportModal, setShowExportModal] = useState<boolean>(false)
  const [showChainExportModal, setShowChainExportModal] =
    useState<boolean>(false)

  const selectedContactCompanyIds = useMemo(() => {
    const accessor = 'contact_company_id'
    return (
      data?.results
        ?.filter((_: any, i: number) => rowSelection[i])
        ?.map((r: any) => {
          return accessor.split('.').reduce((acc, key) => acc[key], r)
        }) ?? []
    )
  }, [data, rowSelection])

  const unselectedContactCompanyIds = useMemo(() => {
    if (!isAllRowsSelected) return []
    return (
      data?.results
        ?.filter((_, i) => !rowSelection[i])
        ?.map((r) => r.contact_company_id) ?? []
    )
  }, [data, rowSelection, isAllRowsSelected])

  const columns = useChainsTableColumnsV2(
    chainsSummary,
    setMenuModal,
    sorting,
    data,
    selectedProductsIds,
    preferences,
    props.baseFilters?.main_campaign
  )
  const hiddenColumnIds = useMemo(() => {
    const visibleIds = new Set(
      columns.map((col) => col.id).filter((id): id is string => !!id)
    )
    return Object.keys(CHAIN_COLUMNS).filter((id) => !visibleIds.has(id))
  }, [columns])

  const refetchChainsSummary = useCallback(() => {
    if (paramsContainAccountFilters(filterAndSortParams)) {
      queryClient.refetchQueries({ queryKey: [CHAIN_SUMMARY_KEY] })
    }
  }, [filterAndSortParams, queryClient])

  const selectedContactCompany = useMemo(() => {
    if (selectedContactCompanyIds.length !== 1 || !data?.results)
      return undefined
    const selectedContactCompany = data.results.find(
      (item) => item.contact_company_id === selectedContactCompanyIds[0]
    )
    return selectedContactCompany
  }, [selectedContactCompanyIds, data?.results])

  const apiTagsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const response = await api.commonManageTags({
        tableType: 'chains-reports-table',
        values,
      })
      refetchChainsSummary()
      return response
    },
    [api, refetchChainsSummary]
  )

  const apiDistributorsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const apiAction = api.commonManageDistributors({
        tableType: 'chains-reports-table',
        values,
      })
      const response = await apiAction
      refetchChainsSummary()
      return response
    },
    [api, refetchChainsSummary]
  )

  const apiGPOsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const apiAction = api.commonManageGPOs({
        tableType: 'chains-reports-table',
        values,
      })
      const response = await apiAction
      refetchChainsSummary()
      return response
    },
    [api, refetchChainsSummary]
  )

  const actions = useMemo(() => {
    const items: IDropdownItem[] = [
      {
        label: 'Create Deals',
        icon: <PiHandshake size={20} />,
        callback: handleCreateDeals,
      },
      {
        label: 'Manage Tags',
        icon: <PiTag size={20} />,
        callback: () => setShowAssignTags(true),
      },
      {
        label: 'Add Known Distributors',
        icon: <PiTruck size={20} />,
        callback: () => setShowAssignDistributors(true),
      },
      {
        label: 'Add GPO',
        icon: <PiShippingContainer size={20} />,
        callback: () => setShowAssignGPOs(true),
      },
      {
        label: 'Schedule Reminder',
        icon: <PiCalendarStar size={20} />,
        callback: () => setShowReminderModal(true),
        disabled: selectedContactCompanyIds.length !== 1,
        disabledTooltipText:
          'Please select exactly one chain to schedule a reminder',
      },
      {
        label:
          selectedContactCompanyIds.length === 1 &&
          pin.isPinned({
            targetType: 'company',
            targetId: selectedContactCompanyIds[0],
          })
            ? 'Unpin from homepage'
            : 'Pin to homepage',
        icon: <PiPushPin size={20} />,
        hidden: !featureFlag.enableQuickAccess,
        callback: () => {
          const id = selectedContactCompanyIds[0]
          const name =
            selectedContactCompany?.chain?.chain ??
            selectedContactCompany?.overriden_name ??
            'target'
          const nowPinned = pin.toggle({
            targetType: 'company',
            targetId: id,
          })
          toast.success(
            nowPinned
              ? `Pinned ${name} to homepage`
              : `Unpinned ${name} from homepage`
          )
        },
        disabled: selectedContactCompanyIds.length !== 1,
        disabledTooltipText:
          'Please select exactly one target to pin to homepage',
      },
      {
        label: 'Export Selected',
        icon: <PiExport size={20} />,
        callback: () => setShowExportModal(true),
      },
      {
        label: 'Push to Salesforce',
        icon: <SiSalesforce size={20} />,
        // Yes, it's vaporware atm.
        callback: () => {
          setTimeout(() => {
            toast.success('Successfully pushed to Salesforce')
          }, 1000)
        },
        hidden: !featureFlag.enablePushToSalesforce,
      },
    ]

    if (featureFlag.enableContactRequests) {
      items.push({
        label: 'Request Contacts',
        icon: <PiPhone size={20} />,
        callback: () => setShowContactRequest(true),
        disabled: totalRowsSelected > MAX_OPPORTUNITIES_REQUESTED,
        disabledTooltipText: `To request contacts, select less than ${MAX_OPPORTUNITIES_REQUESTED} opportunities.`,
      })
    }

    if (featureFlag.enableCampaigns) {
      if (!hasCampaign) {
        items.push({
          label: 'Push to Campaign',
          icon: <PiRocketLaunch size={20} />,
          subContent: (
            <AssignToCampaignModal
              data={data?.results ?? []}
              accessorKey={'contact_company_id'}
              tableQueryKey={TABLE_QUERY_KEY}
              requestParams={filterAndSortParams}
              companyTypeSlug={'restaurants-bars'}
              tableTypeSlug="chains-reports-table"
              buttonLess
            />
          ),
        })
      } else {
        items.push({
          label: 'Unassign from Campaign',
          icon: <PiXCircle size={20} />,
          callback: () => unassignFromCampaign.unassign(),
        })
      }
    }

    return items
  }, [
    data,
    featureFlag,
    hasCampaign,
    selectedContactCompanyIds,
    selectedContactCompany,
    pin,
  ])

  return (
    <DataTableContainer>
      <DataTable
        tableHeader={
          <S.SearchContainer>
            <div className="flex w-full gap-2 items-center">
              <TableSearchWithElastic
                filterStore={chainsFilterStore}
                historyKey="chains"
                options={[
                  {
                    label: 'Menu',
                    searchKey: 'menu',
                    searchPlaceholder: 'Search menus… e.g. chicken',
                    elastic: true,
                    autoSetRelevancySort: false,
                    icon: PiForkKnife,
                  },
                  {
                    label: 'Name',
                    searchKey: 'chain_name',
                    searchPlaceholder:
                      'Search chain names… e.g. Bella’s Bistro',
                    elastic: true,
                    autoSetRelevancySort: true,
                    icon: PiStorefront,
                  },
                  {
                    label: 'Domain',
                    searchKey: 'domain_elastic',
                    searchPlaceholder: 'Search domains… e.g. bellasbistro.com',
                    elastic: false,
                    autoSetRelevancySort: false,
                    icon: PiGlobe,
                  },
                ]}
                onSearchApplied={(option) => {
                  if (option.autoSetRelevancySort) {
                    // Name searches: set to relevancy sort
                    setSorting([{ id: 'relevancy', desc: true }])
                  } else {
                    // All other searches (menu, domain): reset to chain count
                    setSorting([{ id: 'count', desc: true }])
                  }
                }}
              />

              <TableButtonProduct
                selectedProducts={selectedProductsIds}
                onClick={() => setShowProductSelection(true)}
              />
              <ChainsFilterset
                hideCampaignsFilter={false}
                filterIdentifier={IDENTIFIER as FilterIdentifier}
                hiddenColumnIds={hiddenColumnIds}
              />
              <ColumnSelectorRecipient tableKey={props.tableKey} />
              <TableButtonSort
                options={filteredSortOptions}
                onSelect={(value, direction) => {
                  return setSorting([{ id: value, desc: direction === 'desc' }])
                }}
                selectedValue={
                  sorting.length
                    ? {
                        value: sorting[0].id,
                        direction: sorting[0].desc ? 'desc' : 'asc',
                      }
                    : undefined
                }
              />
            </div>

            <div className={cn('flex flex-row justify-between gap-4')}>
              {linkVariant !== 'plain' && (
                <TableButtonAction
                  items={actions}
                  disabled={!totalSelectedRows}
                  selectedRowsCount={totalSelectedRows ?? 0}
                />
              )}
              <FilterChips
                identifier={IDENTIFIER as FilterIdentifier}
                store={chainsFilterStore}
                showActive
                clearAllButton
              />
            </div>
          </S.SearchContainer>
        }
        selectHeaderActions={
          linkVariant === 'plain' ? (
            <TableButtonAction
              items={actions}
              disabled={!totalSelectedRows}
              selectedRowsCount={totalSelectedRows ?? 0}
            />
          ) : undefined
        }
        tableKey={props.tableKey}
        loading={isFetching || unassignFromCampaign.isUnassigning}
        data={data?.results ?? []}
        columns={columns}
        sortableFields={[
          ...sortableFields,
          ...(featureFlag.enableTagsSort ? ['tags'] : []),
        ]}
        virtualizeRows
        enableRowSelection
        enableColumnResizing
        rowHeight={44}
        headerHeight={40}
        stickyFirstColumn={linkVariant === 'plain'}
        isPaginationEnabled={true}
        defaultSort={[{ id: 'count' as any, desc: true }]}
        paginationOptions={{
          totalRows: chainsSummary?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        selectAllText="Select Targets"
        footerControls={
          <OpportunitiesTableFooterControls
            hideCreateCompanyButton={props.hideCreateCompanyButton}
            companyTypeSlug={'restaurants-bars'}
            onDownloadButtonClick={() =>
              selectedTag === 'fb-companies'
                ? setShowChainExportModal(true)
                : setShowExportModal(true)
            }
          />
        }
      />

      <CreateContactRequestJobModal
        data={data?.results ?? []}
        open={showContactRequest}
        onClose={() => setShowContactRequest(false)}
        // v1 and v2 keys
        accessorKeys={['contact_company_id', 'contact_company[0].id']}
        tableQueryKey={TABLE_QUERY_KEY}
        requestParams={filterAndSortParams}
        companyTypeSlug={'restaurants-bars'}
      />

      <TagAssignModal
        show={showAssignTags}
        handleClose={() => setShowAssignTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={apiTagsAction}
        idAccessor="contact_company_id"
      />

      <DistributorAssignModal
        show={showAssignDistributors}
        handleClose={() => setShowAssignDistributors(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="contact_company_id"
        filterAndSortParams={filterAndSortParams}
        apiDistributorsAction={apiDistributorsAction}
      />

      <GPOAssignModal
        show={showAssignGPOs}
        handleClose={() => setShowAssignGPOs(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="contact_company_id"
        filterAndSortParams={filterAndSortParams}
        apiGPOsAction={apiGPOsAction}
      />

      <ChainExportModal
        tableTypeSlug="chains-reports-table"
        count={
          (showExportModal
            ? selectedContactCompanyIds?.length
            : chainsSummary?.count) ?? 0
        }
        show={showExportModal || showChainExportModal}
        _handleClose={() => {
          setShowExportModal(false)
          setShowChainExportModal?.(false)
        }}
        filters={filterAndSortParams}
        productSelected={selectedProductsIds[0]}
        selectedIds={!isAllRowsSelected ? selectedContactCompanyIds : undefined}
        excludeIds={isAllRowsSelected ? unselectedContactCompanyIds : undefined}
      />

      {menuModal?.chainId && (
        <MenuModal
          show={!!menuModal}
          handleClose={() => setMenuModal(undefined)}
          chainId={menuModal.chainId!}
          chainName={menuModal.chainName!}
          menuLength={menuModal?.menuLength}
          filterStore={chainsFilterStore}
        />
      )}

      <CreateDealModalForTable
        show={openDealModal}
        handleClose={() => setOpenDealModal(false)}
        queryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="contact_company_id"
        optimisticUiTarget="chains"
        filterAndSortParams={filterAndSortParams}
        onDealCreated={refetchChainsSummary}
        originCampaignId={props.baseFilters?.main_campaign}
        tableTypeSlug="chains-reports-table"
        companyTypeSlug="restaurants-bars"
      />

      <ChangeProductViewModal
        show={showProductSelection}
        handleClose={() => setShowProductSelection(false)}
      />

      {featureFlag.enableReminders && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultCompanyId={selectedContactCompany?.contact_company_id}
          onCreated={() => {
            setShowReminderModal(false)
          }}
        />
      )}
    </DataTableContainer>
  )
}
